import React, { useState, useEffect, useRef } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { Eye, EyeSlash, InfoCircleFill, Search } from "react-bootstrap-icons";
import PropTypes from "prop-types";
import Select, { components as selectComponents } from "react-select";
import { formatBarcode, generateTimeWindow } from "../../utils/utils";
import DOMPurify from "dompurify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import closeEye from "../../assets/icons/hide-password-icon.svg";
import openEye from "../../assets/icons/show-password-icon.svg";
import barcodeScanner from "../../assets/images/Barcode Scanner.svg"
import infoIcon from "../../assets/icons/Information-fill.svg";
import vhCloseEye from "../../assets/icons/Hide.svg";
import vhOpenEye from "../../assets/icons/Show.svg";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { getSafeImageSrc } from "../../utils/utils.js";

const VhSelectIndicator = (indicatorProps) => (
  <selectComponents.DropdownIndicator {...indicatorProps}>
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d={
          indicatorProps.selectProps.menuIsOpen
            ? "M2.5 10.5L8 5L13.5 10.5"
            : "M2.5 5.5L8 11L13.5 5.5"
        }
        stroke="#05054B"
        strokeWidth="1.5"
      />
    </svg>
  </selectComponents.DropdownIndicator>
);

const VhSelectOption = (optionProps) => (
  <selectComponents.Option {...optionProps}>
    <span>{optionProps.label}</span>
    {optionProps.isSelected && (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="19"
        height="15"
        viewBox="0 0 19 15"
        fill="none"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M18.9142 1.41421L5.70711 14.6213L0 8.91421L1.41421 7.5L5.70711 11.7929L17.5 0L18.9142 1.41421Z"
          fill="#33333F"
        />
      </svg>
    )}
  </selectComponents.Option>
);

/**
 * Reusable input element with various customization options
 * @param {*} props
 * @returns
 */
function FormInput(props) {
  const [password, setPassword] = useState(props.value);
  const [showPassword, setShowPassword] = useState(false);
  const [inputValue, setInputValue] = useState(props.value || "");
  const [changeValue, setChangeValue] = useState(false);
  const selectRef = useRef(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const { featureFlagLoading, featureFlag } = useAuth();
  const getMaxDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setFullYear(tomorrow.getFullYear() - 18);
    return tomorrow;
  };
  const [isOpen, setIsOpen] = useState(false);
  let maxDate = new Date();
  if (props.name === "date_of_birth") {
    maxDate = getMaxDate();
  }
  const handleCalenderButtonClick = (e) => {
    if (e?.target?.classList.contains("react-datepicker__day")) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  };
  useEffect(() => {
    if (props.type === "password") {
      setPassword(props.value);
    }

    if (props.name === "test_type") {
      // Always set input value from props for test_type
      setInputValue(props.value || "");
    } else if (props.useLocalStorage && !initialLoad) {
      const storedValue = localStorage.getItem(props.name);
      if (storedValue) {
        setInputValue(storedValue);
      } else {
        setInputValue(props.value || "");
      }
    } else {
      setInputValue(props.value || "");
    }

    if (props.type === "select") {
      const fieldName = props.name;
      const fieldValue = selectRef.current?.value;
      if (props.useLocalStorage && !initialLoad) {
        localStorage.setItem(fieldName, fieldValue);
      }
    }
    setInitialLoad(false); // Set to false after the first render
  }, [props.value, props.type, props.name, props.useLocalStorage]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleChange = (e) => {
    if (!props.readOnly && !props.disabled) {
      const newValue = e.target.value;
      setInputValue(newValue);

      if (props.name === "primary_barcode") {
        e.target.value = formatBarcode(newValue);
      }

      props.onChange(e);

      if (props.type === "password") {
        setPassword(newValue);
      }

      if (props.useLocalStorage) {
        localStorage.setItem(props.name, newValue);
      }
    }
  };

  const handleBlur = (e) => {
    const fieldName = props.name;
    const fieldValue = e.target.value;

    if (props.useLocalStorage) {
      localStorage.setItem(fieldName, fieldValue);
    }

    if (props.name === "primary_barcode") {
      const formattedBarcode = formatBarcode(fieldValue);
      setInputValue(formattedBarcode);

      props.onChange({
        ...e,
        target: { ...e.target, value: formattedBarcode },
      });
    }

    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const midPoint = Math.ceil(props.passHints?.length / 2);

  const handleSelectChange = (e, selectedOption) => {
    setChangeValue(true);
    if (!props.readOnly && !props.disabled) {
      if (props.type === "dropdown") {
        props.onChange({ target: { name: props.name, value: e?.value || "" } });
        if (e && typeof props.onSelectChange === "function") {
          props.onSelectChange(e);
        }
      } else if (e?.target) {
        props.onChange({ target: { name: props.name, value: e.target.value } });
      }
    }
    if (typeof props.onSelectChange === "function") {
      props.onSelectChange(selectedOption);
    }
  };

  const handleBarcodeChange = (e) => {
    if (!props.readOnly && !props.disabled) {
      let value = e.target.value;
      if (props.name === "primary_barcode") {
        // Clean input and format barcode
        let cleanedValue = value.replace(/[^a-zA-Z0-9]/g, "");
        // Remove non-alphanumeric characters
        let formattedValue = formatBarcode(cleanedValue);

        // Extract the segments to enforce alphabetic input and uppercase for the first segment
        let [firstSegment, secondSegment, remainingSegment] =
          formattedValue.split("-");

        // Prevent non-alphabetic characters in the first and second segments
        if (
          (firstSegment && !/^[A-Z]*$/.test(firstSegment)) ||
          (secondSegment && !/^[a-zA-Z]*$/.test(secondSegment))
        ) {
          return; // Prevent updating if segments contain invalid characters
        }

        // Prevent non-numeric characters in the remaining segment
        if (remainingSegment && !/^\d*$/.test(remainingSegment)) {
          return; // Prevent updating if it contains non-numeric characters
        }

        setInputValue(formattedValue);
        props.onChange({
          ...e,
          target: { ...e.target, value: formattedValue },
        });

        // Store formatted value in localStorage
        localStorage.setItem(props.name, formattedValue);
      } else {
        setInputValue(value);
        props.onChange(e);
      }
    }
  };

  const DropdownIndicator = () => null;

  // Determine the correct value for react-select
  const findOptionByValue = (value) => {
    return props?.options?.find((option) => option?.value === value) || null;
  };

  const newValue = changeValue
    ? findOptionByValue(props.value)
    : findOptionByValue(props.storeName?.[0]?.store_info);

  // Sanitize the help text content
  const sanitizedHelpTextAbove = DOMPurify.sanitize(props.helpTextAbove);
  const sanitizedHelpText = DOMPurify.sanitize(props.helpText);

  const times = generateTimeWindow();
  const showVhBarcodeUi =
    featureFlag &&
    ["primary_barcode", "secondary_barcode"].includes(props.name);
  const isBarcodeField =
    showVhBarcodeUi ||
    props.className?.includes("barcode-tubes") ||
    ["primary_barcode", "secondary_barcode"].includes(props.name);

  const barcodeControl = (
    <Form.Control
      aria-describedby={props.aria_describedby}
      as={props?.as || Form.Control}
      autoComplete={props.autoComplete}
      disabled={props.disabled || props.isdisabled}
      isInvalid={props.touched && !!props.error}
      maxLength={props.maxLength}
      name={props.name}
      onBlur={handleBlur}
      onChange={
        props.name === "primary_barcode" ? handleBarcodeChange : handleChange
      }
      onKeyDown={props.onKeyDown}
      placeholder={props.placeholder}
      readOnly={props.readOnly}
      required={props.required}
      size={props.size}
      type={props.type}
      value={inputValue}
      className={
        props.name === "repeated_test" && String(props.value).includes("Yes")
          ? "repeated-test-style"
          : ""
      }
    />
  );
  return (
    <Form.Group
      className={`${props.className || ""}${showVhBarcodeUi ? " vh-barcode-group" : ""}`.trim()}
      controlId={props.controlId}
      key={props.key}
    >
      {props.label && props.label !== "" && (
        <Form.Label className="labels">
          {props.required ? (
            <>
              {props.label}
              <span className="asterisk">*</span>
            </>
          ) : (
            props.label
          )}
        </Form.Label>
      )}

      {props.helpTextAbove && (
        <Form.Text id={props.aria_describedby} muted={props.muted}>
          <span
            className="help-text-above"
            dangerouslySetInnerHTML={{ __html: sanitizedHelpTextAbove }}
          />
        </Form.Text>
      )}
      {props.type !== "select" &&
        props.type !== "dropdown" &&
        props.type !== "password" &&
        props.type !== "selectValue" &&
        props?.type !== "date" &&
        props?.type !== "time" && (
          <div
            className={`d-flex gap-3 ${showVhBarcodeUi ? "vh-barcode-row" : ""}`}
          >
            {showVhBarcodeUi ? (
              <div className="vh-barcode-input-wrap">
                <img src={barcodeScanner} alt="barcode scanner" />
                {barcodeControl}
              </div>
            ) : (
              barcodeControl
            )}

            {props.image && getSafeImageSrc(props.image) && (
              <img
                className={`barcode-img ${
                  showVhBarcodeUi
                    ? props.name === "secondary_barcode"
                      ? "barcode-img--lavender"
                      : "barcode-img--gold"
                    : ""
                }`}
                alt="Barcode"
                src={getSafeImageSrc(props.image)}
              />
            )}
          </div>
        )}
      {props.type === "password" && (
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Form.Control
            aria-describedby={props.aria_describedby}
            as={props?.as || Form.Control}
            autoComplete={props.autoComplete}
            disabled={props.disabled}
            isInvalid={props.touched && !!props.error}
            name={props.name}
            onBlur={handleBlur}
            onChange={handleChange}
            placeholder={props.placeholder}
            readOnly={props.readOnly}
            required={props.required}
            size={props.size}
            type={showPassword ? "text" : "password"}
            value={password}
            style={{ padding: "10px 40px 10px 16px" }}
            maxLength={props.maxLength}
          />
          <button
            type="button"
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? "Hide password" : "Show password"}
            className={props.eyeToggleClass}
            style={{
              position: "absolute",
              right: "10px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              color: "#6c757d",
              background: "none",
              border: "none",
              padding: 0,
            }}
          >
            <img
              src={
                showPassword
                  ? featureFlag
                    ? vhOpenEye
                    : openEye
                  : featureFlag
                    ? vhCloseEye
                    : closeEye
              }
              alt={showPassword ? "Show password" : "Hide password"}
              width={featureFlag ? 24 : showPassword ? 19 : 18}
              height={featureFlag ? 24 : showPassword ? 19 : 18}
            />
            {props.showToggleLabel && (<span>{showPassword ? "Hide" : "Show"}</span>)}
          </button>
        </div>
      )}
      {(props.type === "selectValue" || (props.type === "select" && !featureFlag)) && (
        <Form.Select
          {...props}
          aria-label="Default select example"
          disabled={props.isdisabled || props.disabled}
          isInvalid={props.touched && !!props.error}
          onChange={(e) =>
            handleSelectChange(
              e,
              props.options.find((option) => option.value === e.target.value),
            )
          }
          placeholder={props.placeholder}
          ref={selectRef}
          value={props.value}
        >
          <option value="">{props.placeholder}</option>
          {props.options.map((option) => (
            <option key={option.value} value={option.value}>
              {props.type === "select" ? option : option.label}
            </option>
          ))}
        </Form.Select>
      )}
      {props.type === "select" && featureFlag && (
        <Select
          inputId={props.controlId || props.name}
          name={props.name}
          className={`vh-select ${props.touched && props.error ? "is-invalid" : ""}`}
          classNamePrefix="vh-select"
          components={{
            DropdownIndicator: VhSelectIndicator,
            IndicatorSeparator: null,
            Option: VhSelectOption,
          }}
          isDisabled={props.isdisabled || props.disabled}
          isSearchable={false}
          onBlur={() =>
            props.onBlur?.({ target: { name: props.name, value: props.value } })
          }
          onChange={(option) =>
            handleSelectChange(
              { target: { name: props.name, value: option?.value || "" } },
              option?.value,
            )
          }
          options={props.options.map((option) => ({
            value: option,
            label: option,
          }))}
          placeholder={props.placeholder}
          value={
            props.value ? { value: props.value, label: props.value } : null
          }
        />
      )}
      {props.type === "dropdown" && (
        <Select
          className="location-dropdown"
          components={{ DropdownIndicator }}
          isClearable
          isSearchable
          onBlur={handleBlur}
          onChange={handleSelectChange}
          options={props.options}
          value={newValue} // newValue should be in the format { value: '1000', label: 'Piccadilly Circus' }
        />
      )}
      {props?.type === "time" && (
        <button
          type="button"
          className="datepicker-wrapper m-0 p-0 w-100 btn"
          onClick={(e) => {
            handleCalenderButtonClick(e);
          }}
        >
          <DatePicker
            open={isOpen}
            onClickOutside={() => setIsOpen(false)}
            selected={(() => {
              // Handle different value formats properly
              if (!props.value) return null;

              // If props.value is already a Date object
              if (props.value instanceof Date) return props.value;

              // If props.value is a string in HH:MM format
              if (
                typeof props.value === "string" &&
                /^\d{1,2}:\d{2}$/.test(props.value)
              ) {
                const [hours, minutes] = props.value.split(":").map(Number);
                const date = new Date();
                date.setHours(hours, minutes, 0, 0);
                return date;
              }

              // If it's another format we can't handle, return null
              return null;
            })()}
            isClearable={true}
            onChange={(time) => {
              // Handle null case (when cleared)
              if (!time) {
                props.onChange({
                  target: { name: props.name, value: "" },
                });
                return;
              }
              // Format the time as HH:MM with leading zeros
              const formattedTime = `${time
                .getHours()
                .toString()
                .padStart(2, "0")}:${time
                .getMinutes()
                .toString()
                .padStart(2, "0")}`;
              if (props.useLocalStorage) {
                localStorage.setItem(props.name, formattedTime);
              }
              props.onChange({
                target: { name: props.name, value: formattedTime },
              });
            }}
            onChangeRaw={(e) => {
              let input = e.target?.value?.replace(/\D/g, "");
              let formattedInput = input;

              if (input.length > 2) {
                formattedInput = `${input.slice(0, 2)}:${input.slice(2, 4)}`;
              }

              e.target.value = formattedInput;

              if (formattedInput.length === 5) {
                const [hoursStr, minutesStr] = formattedInput.split(":");
                const hours = Number.parseInt(hoursStr, 10);
                const minutes = Number.parseInt(minutesStr, 10);

                if (
                  !Number.isNaN(hours) &&
                  !Number.isNaN(minutes) &&
                  hours >= 0 &&
                  hours <= 23 &&
                  minutes >= 0 &&
                  minutes <= 59
                ) {
                  const date = new Date();
                  date.setHours(hours, minutes, 0, 0);
                  const formatted = `${hours
                    .toString()
                    .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
                  props.onChange({
                    target: { name: props.name, value: formatted },
                  });
                } else {
                  e.target.value = "";
                  props.onChange({
                    target: { name: props.name, value: "" },
                  });
                }
              }
            }}
            includeTimes={times}
            showTimeSelect
            showTimeSelectOnly
            timeFormat="HH:mm"
            timeIntervals={1}
            dateFormat="HH:mm"
            className="form-control"
            placeholderText={props.placeholder || "HH:MM"}
            customInput={<input maxLength={5} />}
          />
        </button>
      )}

      {props?.type === "date" && (
        <button
          type="button"
          className="datepicker-wrapper m-0 p-0 w-100 btn"
          onClick={(e) => {
            handleCalenderButtonClick(e);
          }}
        >
          <DatePicker
            selected={props.value || null}
            isClearable={true}
            onChange={(date) => {
              // Format the date as YYYY-MM-DD
              const formattedDate = date
                ? `${date.getFullYear()}-${(date.getMonth() + 1)
                    .toString()
                    .padStart(2, "0")}-${date
                    .getDate()
                    .toString()
                    .padStart(2, "0")}`
                : "";
              if (props.useLocalStorage) {
                localStorage.setItem(props.name, formattedDate);
              }
              props.onChange({
                target: { name: props.name, value: formattedDate },
              });
            }}
            onChangeRaw={(e) => {
              let input = e.target?.value?.replace(/\D/g, ""); // Remove non-numeric characters
              let formattedInput = input;

              if (input?.length > 2 && input?.length <= 4) {
                formattedInput = `${input.slice(0, 2)}/${input.slice(2)}`;
              } else if (input?.length > 4) {
                formattedInput = `${input.slice(0, 2)}/${input.slice(
                  2,
                  4,
                )}/${input.slice(4, 8)}`;
              }

              e.target.value = formattedInput;

              // Validate and update DatePicker if length is 10 (dd/MM/yyyy)
              if (formattedInput?.length === 10) {
                let [day, month, year] = formattedInput.split("/");
                const parsedDate = new Date(`${year}-${month}-${day}`);

                if (
                  parsedDate &&
                  parsedDate.getFullYear().toString() === year &&
                  parsedDate.getMonth() + 1 === Number.parseInt(month, 10) &&
                  parsedDate.getDate() === Number.parseInt(day, 10)
                ) {
                  props.onChange({
                    target: { name: props.name, value: formattedInput },
                  });
                } else {
                  e.target.value = ""; // Clear invalid input
                }
              }
            }}
            open={isOpen}
            onClickOutside={() => setIsOpen(false)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            placeholderText={
              props.placeholder ? props.placeholder : "DD/MM/YYYY"
            }
            showMonthDropdown
            showYearDropdown
            scrollableYearDropdown
            yearDropdownItemNumber={125}
            maxDate={maxDate}
          />
        </button>
      )}
      {props.passHints && (
        <div className="d-flex">
          <ul>
            {props.passHints.slice(0, midPoint).map((passHint, index) => (
              <li
                className={props.error === passHint ? "highlights" : ""}
                key={passHint}
              >
                {passHint}
              </li>
            ))}
          </ul>
          <ul>
            {props.passHints.slice(midPoint).map((passHint, index) => (
              <li
                className={props.error === passHint ? "highlights" : ""}
                key={passHint}
              >
                {passHint}
              </li>
            ))}
          </ul>
        </div>
      )}
      {isBarcodeField ? (
        (sanitizedHelpText ||
          (props?.lottNumber && props.name === "primary_barcode")) && (
          <Form.Text
            id={props.aria_describedby}
            muted={props.muted}
            className="vh-barcode-info-msg"
          >
            <img src={infoIcon} alt="" className="vh-barcode-info-icon" />
            <span className="vh-barcode-info-text">
              {sanitizedHelpText && (
                <span
                  className={props.helptextClass}
                  dangerouslySetInnerHTML={{ __html: sanitizedHelpText }}
                />
              )}
              {props?.lottNumber && props.name === "primary_barcode" && (
                <span className={`${props.helptextClass} ms-1`}>
                  {props.lottNumber}
                </span>
              )}
            </span>
          </Form.Text>
        )
      ) : (
        <>
          <div>
            <Form.Text id={props.aria_describedby} muted={props.muted}>
              <span
                className={props.helptextClass}
                dangerouslySetInnerHTML={{ __html: sanitizedHelpText }}
              />
            </Form.Text>
          </div>
          <div>
            {props?.lottNumber && props.name === "primary_barcode" && (
              <Form.Text id={props.aria_describedby} muted={props.muted}>
                <span className={`${props.helptextClass} bold-text`}>
                  {props.lottNumber}
                </span>
              </Form.Text>
            )}
          </div>
        </>
      )}
      <Form.Control.Feedback type="invalid">
        {props.error && (
          <>
            <InfoCircleFill /> {props.error}
          </>
        )}
      </Form.Control.Feedback>
      {props.links && (
        <div className="d-flex forget-password-link">{props.links}</div>
      )}
    </Form.Group>
  );
}

FormInput.propTypes = {
  label: PropTypes.string,
  type: PropTypes.string,
  as: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(["sm", "text", "lg"]),
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  aria_describedby: PropTypes.string,
  helpText: PropTypes.string,
  helpTextAbove: PropTypes.string,
  controlId: PropTypes.string,
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  value: PropTypes.any.isRequired,
  touched: PropTypes.bool,
  error: PropTypes.string,
  helptextClass: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  muted: PropTypes.bool,
  image: PropTypes.string,
  passHints: PropTypes.arrayOf(PropTypes.string),
  links: PropTypes.node,
  handleBarcodeChange: PropTypes.func,
  name: PropTypes.string,
  useLocalStorage: PropTypes.bool,
  storeName: PropTypes.array,
  onSelectChange: PropTypes.func,
  key: PropTypes.string,
  maxLength: PropTypes.number,
  onKeyDown: PropTypes.func,
  lottNumber: PropTypes.string,
  isdisabled: PropTypes.bool,
  showToggleLabel: PropTypes.bool,
  autoComplete: PropTypes.string,
};

FormInput.defaultProps = {
  label: "Email",
  type: "email",
  required: true,
  placeholder: "Enter your email address",
  size: "text",
  readOnly: false,
  disabled: false,
  helpTextAbove: "",
  controlId: "",
  className: "",
  touched: false,
  error: "",
  muted: false,
};

export default FormInput;
